import React, { useState } from 'react';
import { authAPI } from '../lib/api';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function TOUModal({ onAccepted }) {
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleAccept = async () => {
        if (!agreed) { toast.error('Please read and agree to the Terms of Use'); return; }
        setSubmitting(true);
        try {
            let locationData = { latitude: null, longitude: null, location_text: '' };
            // Try to get browser geolocation
            try {
                const pos = await new Promise((resolve, reject) =>
                    navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
                );
                locationData.latitude = pos.coords.latitude;
                locationData.longitude = pos.coords.longitude;
                locationData.location_text = `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`;
            } catch {
                locationData.location_text = 'Location not available';
            }

            await authAPI.acceptTOU(locationData);
            toast.success('Terms of Use accepted');
            onAccepted();
        } catch {
            toast.error('Failed to record acceptance. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="tou-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
                <div className="px-6 py-5 border-b border-slate-200 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <ShieldCheck className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Terms of Use</h2>
                        <p className="text-sm text-slate-500">Please review and accept to continue</p>
                    </div>
                </div>

                <ScrollArea className="h-[400px] px-6 py-4" data-testid="tou-content">
                    <div className="prose prose-sm prose-slate max-w-none">
                        <h3 className="text-lg font-semibold text-slate-800">MedPrep AI Terms of Use</h3>
                        <p className="text-sm text-slate-500 mb-4">Effective Date: March 2026 | Version 1.0</p>

                        <h4 className="font-semibold text-slate-800 mt-4">1. Acceptance of Terms</h4>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            By accessing and using MedPrep AI ("the Platform"), you acknowledge that you have read,
                            understood, and agree to be bound by these Terms of Use. If you do not agree to these terms,
                            you must discontinue use of the Platform immediately.
                        </p>

                        <h4 className="font-semibold text-slate-800 mt-4">2. No Guarantee of Academic Success</h4>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            <strong>MedPrep AI makes no claims, representations, or guarantees regarding the passing of
                            any course, examination, certification, or licensing requirement.</strong> The Platform is
                            designed as a supplementary educational tool and study aid only. Academic outcomes depend on
                            numerous factors beyond the scope of this Platform, including but not limited to individual
                            study habits, prior knowledge, institutional requirements, and examination conditions.
                        </p>

                        <h4 className="font-semibold text-slate-800 mt-4">3. Use at Your Own Risk</h4>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            <strong>Users access and use the Platform entirely at their own risk and discretion.</strong> The
                            content provided, including AI-generated questions, study plans, and performance analytics,
                            is for educational and informational purposes only. It should not be considered a substitute
                            for formal instruction, professional medical training, or official course materials.
                        </p>

                        <h4 className="font-semibold text-slate-800 mt-4">4. AI-Generated Content Disclaimer</h4>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            The Platform utilizes artificial intelligence to generate educational content, including but
                            not limited to practice questions, explanations, and study recommendations. While we strive
                            for accuracy, AI-generated content may contain errors, inaccuracies, or outdated information.
                            Users should independently verify all information and not rely solely on AI-generated content
                            for clinical decision-making or patient care.
                        </p>

                        <h4 className="font-semibold text-slate-800 mt-4">5. Limitation of Liability</h4>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            To the maximum extent permitted by applicable law, MedPrep AI, its creators, affiliates,
                            and contributors shall not be liable for any direct, indirect, incidental, consequential,
                            or punitive damages arising from your use of the Platform, including but not limited to
                            academic outcomes, examination results, or any decisions made based on Platform content.
                        </p>

                        <h4 className="font-semibold text-slate-800 mt-4">6. User Responsibilities</h4>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            Users are responsible for: (a) maintaining the confidentiality of their account credentials;
                            (b) using the Platform in compliance with all applicable laws and institutional policies;
                            (c) not sharing, distributing, or reproducing Platform content without authorization;
                            (d) using the Platform as a supplementary tool alongside formal educational resources.
                        </p>

                        <h4 className="font-semibold text-slate-800 mt-4">7. Data Collection</h4>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            By accepting these terms, you consent to the collection and storage of your acceptance
                            record, including timestamp and approximate location data, for compliance and record-keeping
                            purposes.
                        </p>

                        <h4 className="font-semibold text-slate-800 mt-4">8. Modifications to Terms</h4>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            MedPrep AI reserves the right to modify these Terms of Use at any time. Users will be
                            notified of significant changes and may be required to re-accept updated terms.
                        </p>
                    </div>
                </ScrollArea>

                <div className="px-6 py-4 border-t border-slate-200 space-y-4 bg-slate-50">
                    <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="tou-agree-checkbox">
                        <Checkbox checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" />
                        <span className="text-sm text-slate-700 leading-snug">
                            I have read, understood, and agree to the Terms of Use. I acknowledge that MedPrep AI
                            makes no guarantees of academic success and that I use the Platform at my own risk.
                        </span>
                    </label>
                    <Button
                        onClick={handleAccept}
                        disabled={!agreed || submitting}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 h-11"
                        data-testid="tou-accept-btn"
                    >
                        {submitting ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Recording acceptance...</>
                        ) : (
                            <><ShieldCheck className="h-4 w-4 mr-2" />Accept and Continue</>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
